// Codes clients et fournisseurs de l'expert-comptable — mode « reprise d'activité » (28/10/2026).
// Réglage du mode (administrateur), import du fichier « nom ; code » (aperçu puis
// enregistrement), saisie d'un code, puis renommage des comptes clients : aperçu
// (écritures, lettrages, relevé, règles), confirmation par l'administrateur.
// Fusion des fiches en double (doublons au même nom, ou fiches choisies) :
// aperçu, confirmation par l'administrateur ; la fiche fusionnée est désactivée.
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import OrphanTiersPanel from "../components/OrphanTiersPanel";

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = () => reject(new Error("Lecture du fichier impossible."));
  r.readAsDataURL(file);
});

const STATUS_COLORS = { "à jour": "#166534", "à renseigner": "#92400e", "à renommer": "#1d4ed8", "appliqué à la première écriture": "#4b5563" };
const box = { background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13 };
const ok = { background: "#dcfce7", color: "#166534", borderRadius: 10, padding: "8px 12px", marginBottom: 12, fontSize: 13 };

export default function TiersCodesTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = ["admin", "comptable"].includes(user?.role);
  const [data, setData] = useState(null);
  const [payload, setPayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [plan, setPlan] = useState(null);
  const [edits, setEdits] = useState({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [dups, setDups] = useState([]);
  const [mergeSel, setMergeSel] = useState(null); // { target, sources: [] } pendant le choix
  const [mergePlan, setMergePlan] = useState(null);
  const [view, setView] = useState("clients"); // clients | fournisseurs
  const [onlyPending, setOnlyPending] = useState(false); // filtre « code expert à attribuer »
  const [pdfProps, setPdfProps] = useState(null); // propositions « client → code lu sur le PDF »
  const [pdfSel, setPdfSel] = useState({});

  const load = useCallback(() => {
    api.tiersCodes.get().then(setData).catch(e => setError(e.message));
    if (canEdit) api.tiersCodes.duplicates().then(setDups).catch(() => setDups([]));
  }, [canEdit]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true); setError(""); setMessage("");
    try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const setMode = (mode) => {
    const text = mode === "reprise"
      ? "Passer en reprise d'activité ? Chaque nouveau client devra recevoir le code de l'expert-comptable (ex. 411CARBL) ; plus aucun compte client numéroté automatiquement."
      : "Revenir en création d'activité ? Les nouveaux clients recevront à nouveau un compte numéroté automatiquement (411001…).";
    if (window.confirm(text)) run(async () => { await api.tiersCodes.setMode(mode); load(); });
  };
  const choose = (file) => file && run(async () => {
    setPreview(null);
    const d = { filename: file.name, content_base64: await toBase64(file) };
    setPayload(d);
    setPreview(await api.tiersCodes.import({ ...d, dry_run: true }));
  });
  const applyImport = () => run(async () => {
    const out = await api.tiersCodes.import({ ...payload, dry_run: false });
    setPreview(null); setPlan(null); load();
    setMessage(`✓ ${out.applied.length} code(s) enregistré(s). Les comptes ne sont pas encore renommés : voir « Renommer les comptes… ».`);
  });
  const saveCode = (row) => run(async () => {
    if (row.key.startsWith("s")) await api.tiersCodes.setSupplierCode(row.id, edits[row.key]);
    else await api.tiersCodes.setCode(row.id, edits[row.key]);
    setEdits(e => { const n = { ...e }; delete n[row.key]; return n; });
    setPlan(null); load();
    setMessage(`✓ Code de ${row.name} enregistré.`);
  });
  const loadPlan = () => run(async () => setPlan(await api.tiersCodes.renamePlan()));
  const confirmRename = () => {
    if (!window.confirm(`Renommer ${plan.ready} compte(s) de tiers ? Les écritures, lettrages, lignes de relevé et règles apprises passent au code de l'expert-comptable ; les montants ne changent pas. Une sauvegarde est faite avant.`)) return;
    run(async () => {
      const out = await api.tiersCodes.rename(plan.fingerprint);
      setPlan(null); load();
      setMessage(`✓ ${out.renamed.length} compte(s) renommé(s), ${out.lines} ligne(s) d'écriture.`);
    });
  };

  const loadPdfProps = () => run(async () => {
    const p = await api.tiersCodes.pdfProposals();
    setPdfProps(p);
    setPdfSel(Object.fromEntries(p.items.filter(i => i.confirmable && i.status === "à confirmer").map(i => [i.customer_id, true])));
  });
  const applyPdf = () => {
    const ids = Object.keys(pdfSel).filter(k => pdfSel[k]).map(Number);
    if (!window.confirm(`Enregistrer le code lu sur le PDF pour ${ids.length} client(s) ? Les comptes ne sont pas renommés ici : voir « Renommer les comptes… ».`)) return;
    run(async () => {
      const out = await api.tiersCodes.pdfApply(ids);
      setPdfProps(null); setPlan(null); load();
      setMessage(`✓ ${out.applied.length} code(s) enregistré(s)${out.skipped.length ? `, ${out.skipped.length} écarté(s) (données changées)` : ""}. Vérifier ensuite « Renommer les comptes… ».`);
    });
  };

  const openMerge = (target, sources) => { setMergeSel({ target, sources }); setMergePlan(null); setError(""); setMessage(""); };
  const previewMerge = () => run(async () => setMergePlan(await api.tiersCodes.mergePreview(Number(mergeSel.target), mergeSel.sources.map(Number))));
  const confirmMerge = () => {
    const names = mergePlan.sources.map(s => s.name).join(", ");
    if (!window.confirm(`Fusionner ${names} dans « ${mergePlan.target.name} » ? Factures, contacts et écritures passent à la fiche conservée ; la fiche fusionnée est désactivée (jamais supprimée). Une sauvegarde est faite avant.`)) return;
    run(async () => {
      const out = await api.tiersCodes.merge(mergePlan.target.customer_id, mergePlan.sources.map(s => s.customer_id), mergePlan.fingerprint);
      setMergePlan(null); setMergeSel(null); setPlan(null); load();
      setMessage(`✓ ${out.merged.length} fiche(s) fusionnée(s) dans ${out.target.name}${out.lines ? `, ${out.lines} ligne(s) d'écriture reportée(s)` : ""}. Vérifier ensuite « Renommer les comptes… ».`);
    });
  };

  if (!data) return error ? <div style={{ color: "#e24b4a", fontSize: 13 }}>{error}</div> : <div style={{ fontSize: 13, color: "#4b5563" }}>Chargement…</div>;
  const reprise = data.mode === "reprise";
  const q = search.trim().toLowerCase();
  const suppliers = view === "fournisseurs";
  const list = suppliers
    ? (data.suppliers || []).map(s => ({ ...s, key: `s${s.supplier_account_id}`, id: s.supplier_account_id, name: s.supplier }))
    : data.clients.map(c => ({ ...c, key: `c${c.customer_id}`, id: c.customer_id, name: c.client }));
  const PENDING = "code expert à attribuer";
  const pendingCount = (l) => l.filter(c => c.flags.includes(PENDING)).length;
  const pendingTotal = pendingCount(data.clients) + pendingCount(data.suppliers || []);
  const shownList = onlyPending ? list.filter(c => c.flags.includes(PENDING)) : list;
  const rows = q ? shownList.filter(c => c.name.toLowerCase().includes(q) || (c.code || "").toLowerCase().includes(q) || (c.account || "").toLowerCase().includes(q)) : shownList;
  const count = (s) => list.filter(c => c.status === s).length;

  return (
    <div>
      {/* Codes 401/411 sans fiche (T, 03/11/2026) */}
      <OrphanTiersPanel />
      <div style={box}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{reprise ? "Reprise d'activité : codes de l'expert-comptable" : "Création d'activité : comptes clients numérotés automatiquement"}</div>
        <div style={{ color: "#4b5563" }}>
          {reprise
            ? "Chaque client reçoit le code de son compte chez l'expert-comptable (ex. 411CARBL). Un nouveau client ne peut pas être enregistré sans son code."
            : "Chaque nouveau client reçoit un compte 411001, 411002… Choisir la reprise d'activité si l'expert-comptable a déjà des codes clients (société reprise d'un autre logiciel)."}
        </div>
        {reprise && pendingTotal > 0 && (
          <div style={{ marginTop: 6, color: "#92400e" }}>
            ⚠ {pendingTotal} tiers avec un compte automatique : <strong>code expert à attribuer</strong> ({pendingCount(data.clients)} client(s), {pendingCount(data.suppliers || [])} fournisseur(s)).{" "}
            <button className="btn" style={{ padding: "1px 8px", fontSize: 12 }} onClick={() => { setOnlyPending(true); setView(pendingCount(data.clients) ? "clients" : "fournisseurs"); }}>Afficher</button>
          </div>
        )}
        {isAdmin && <button className="btn" style={{ marginTop: 8 }} disabled={busy} onClick={() => setMode(reprise ? "creation" : "reprise")}>{reprise ? "Revenir en création d'activité" : "Passer en reprise d'activité"}</button>}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: "#4b5563" }}>
          {list.length} {suppliers ? "fournisseur(s)" : "client(s)"} · {count("à jour")} à jour · {count("à renseigner")} à renseigner · {count("à renommer")} à renommer
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <label className="btn" style={{ cursor: busy ? "wait" : "pointer" }}>
              Importer le fichier des tiers (nom ; code)
              <input type="file" accept=".csv,.txt,.xlsx,.xls" style={{ display: "none" }} disabled={busy} onChange={e => { choose(e.target.files[0]); e.target.value = ""; }} />
            </label>
            <button className="btn" disabled={busy} onClick={loadPdfProps}>Codes lus sur les PDF…</button>
            <button className="btn" disabled={busy || data.clients.length < 2} onClick={() => openMerge(String(data.clients[0].customer_id), [])}>Fusionner des fiches…</button>
            <button className="btn" disabled={busy} onClick={loadPlan}>Renommer les comptes…</button>
          </div>
        )}
      </div>

      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 10, fontSize: 13 }}>{error}</div>}
      {message && <div style={ok}>{message}</div>}

      {preview && (
        <div style={{ ...box, background: "#f0fdf9" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Aperçu — rien n'est encore enregistré</div>
          <div style={{ marginBottom: 6 }}>{preview.lines.length} ligne(s) lue(s) · <strong>{preview.applicable} code(s) à enregistrer</strong>. Une ligne 411… est un client, une ligne 401… un fournisseur ; chacun est retrouvé par son nom, jamais au hasard. Un fournisseur inconnu est créé avec son code.</div>
          <table className="table" style={{ width: "100%", fontSize: 12 }}>
            <thead><tr><th style={{ textAlign: "left" }}>Ligne</th><th style={{ textAlign: "left" }}>Nom (fichier)</th><th style={{ textAlign: "left" }}>Code</th><th style={{ textAlign: "left" }}>Client / fournisseur</th><th style={{ textAlign: "left" }}>Code actuel</th><th style={{ textAlign: "left" }}>Résultat</th></tr></thead>
            <tbody>{preview.lines.map(l => (
              <tr key={l.line}><td>{l.line}</td><td>{l.name}</td><td>{l.code}</td><td>{l.customer || l.supplier || "—"}</td><td>{l.current_code || "—"}</td>
                <td style={{ color: ["à appliquer", "code remplacé", "fournisseur à créer"].includes(l.status) ? "#166534" : l.status === "déjà à jour" ? "#4b5563" : "#92400e" }}>{l.status}</td></tr>
            ))}</tbody>
          </table>
          <button className="btn primary" style={{ marginTop: 8 }} disabled={busy || !preview.applicable} onClick={applyImport}>Enregistrer {preview.applicable} code(s)</button>
        </div>
      )}

      {pdfProps && (
        <div style={{ ...box, background: "#f0fdf9" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Codes lus sur les PDF Dolibarr importés — rien n'est encore enregistré</div>
          <div style={{ marginBottom: 6 }}>
            {pdfProps.read} facture(s) portent la ligne « Code comptable client »{pdfProps.scanned ? ` (${pdfProps.scanned} PDF lus à l'instant)` : ""} · {pdfProps.items.length} client(s)
            {pdfProps.without_customer ? ` · ${pdfProps.without_customer} facture(s) sans fiche client (le code sera proposé à leur validation)` : ""}.
            {pdfProps.remaining > 0 && <> <strong>{pdfProps.remaining} PDF restent à lire</strong> : <button className="btn" style={{ padding: "1px 8px", fontSize: 12 }} disabled={busy} onClick={loadPdfProps}>Continuer la lecture</button></>}
            Les cas signalés (client à deux codes, code lu sur deux fiches, code déjà attribué) ne s'enregistrent pas d'ici.
          </div>
          <table className="table" style={{ width: "100%", fontSize: 12 }}>
            <thead><tr><th></th><th style={{ textAlign: "left" }}>Client</th><th style={{ textAlign: "left" }}>Code de la fiche</th><th style={{ textAlign: "left" }}>Code lu</th><th style={{ textAlign: "left" }}>Factures sources</th><th style={{ textAlign: "left" }}>Résultat</th></tr></thead>
            <tbody>{pdfProps.items.filter(i => i.status !== "identique").map(i => (
              <tr key={i.customer_id}>
                <td>{i.confirmable && <input type="checkbox" checked={!!pdfSel[i.customer_id]} onChange={e => setPdfSel(s => ({ ...s, [i.customer_id]: e.target.checked }))} />}</td>
                <td>{i.customer}</td><td>{i.fiche_code || "—"}</td>
                <td>{i.codes.map(c => c.code).join(" / ") || "—"}</td>
                <td>{i.codes.map(c => `${c.invoices.join(", ")}${c.count > c.invoices.length ? "…" : ""}`).join(" / ")}</td>
                <td style={{ color: i.confirmable ? "#166534" : "#92400e" }}>{i.status}{i.flags.length ? ` · ⚠ ${i.flags.join(" · ")}` : ""}</td>
              </tr>
            ))}</tbody>
          </table>
          <div style={{ marginTop: 6, color: "#4b5563" }}>{pdfProps.items.filter(i => i.status === "identique").length} client(s) déjà au même code (non affichés).</div>
          {isAdmin
            ? <button className="btn primary" style={{ marginTop: 8 }} disabled={busy || !Object.values(pdfSel).some(Boolean)} onClick={applyPdf}>✓ Enregistrer {Object.values(pdfSel).filter(Boolean).length} code(s)</button>
            : <div style={{ marginTop: 8, color: "#4b5563" }}>La confirmation est réservée à l'administrateur de la société.</div>}
          <button className="btn" style={{ marginTop: 8, marginLeft: 8 }} onClick={() => setPdfProps(null)}>Fermer</button>
        </div>
      )}

      {canEdit && (dups.length > 0 || mergeSel) && (
        <div style={{ ...box, background: "#fffbeb" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Fiches clients en double</div>
          {dups.length > 0 && <div style={{ marginBottom: 6 }}>{dups.length} groupe(s) de fiches au même nom. Fusionner les doublons avant de renommer les comptes.</div>}
          {dups.map((g, k) => (
            <div key={k} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
              <span>{g.map(c => `${c.name} (${c.account || "sans compte"}${c.code ? ` · ${c.code}` : ""} · ${c.invoices} facture(s))`).join(" / ")}</span>
              <button className="btn" style={{ padding: "2px 8px" }} disabled={busy} onClick={() => openMerge(String(g[0].customer_id), g.slice(1).map(c => String(c.customer_id)))}>Fusionner…</button>
            </div>
          ))}
          {mergeSel && (
            <div style={{ marginTop: 8, borderTop: "1px solid #fde68a", paddingTop: 8 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span>Fiche conservée :</span>
                <select className="input" value={mergeSel.target} onChange={e => { const t = e.target.value; setMergeSel(s => ({ target: t, sources: s.sources.filter(x => x !== t) })); setMergePlan(null); }}>
                  {data.clients.map(c => <option key={c.customer_id} value={String(c.customer_id)}>{c.client}{c.account ? ` (${c.account})` : ""}</option>)}
                </select>
                <span>Fiche(s) à fusionner :</span>
                <select className="input" multiple size={Math.min(5, data.clients.length)} value={mergeSel.sources} onChange={e => { setMergeSel(s => ({ ...s, sources: [...e.target.selectedOptions].map(o => o.value).filter(v => v !== s.target) })); setMergePlan(null); }}>
                  {data.clients.filter(c => String(c.customer_id) !== mergeSel.target).map(c => <option key={c.customer_id} value={String(c.customer_id)}>{c.client}{c.account ? ` (${c.account})` : ""}</option>)}
                </select>
                <button className="btn" disabled={busy || !mergeSel.sources.length} onClick={previewMerge}>Aperçu de la fusion</button>
                <button className="btn" disabled={busy} onClick={() => { setMergeSel(null); setMergePlan(null); }}>Fermer</button>
              </div>
              {mergePlan && (
                <div style={{ marginTop: 8 }}>
                  <div>Fiche conservée : <strong>{mergePlan.target.name}</strong> ({mergePlan.target.account || "sans compte"}) · code de l'expert-comptable après fusion : <strong>{mergePlan.final_code || "aucun"}</strong></div>
                  {mergePlan.target.blockers.map(b => <div key={b} style={{ color: "#b91c1c" }}>⚠ {b}</div>)}
                  {mergePlan.sources.map(s => (
                    <div key={s.customer_id} style={{ marginTop: 4 }}>
                      <strong>{s.name}</strong> : {Object.entries(s.counts).map(([k, n]) => `${n} ${k}`).join(", ") || "aucune référence"}
                      {s.accounting && <> · compte {s.accounting.from_code} : {s.accounting.action}{s.accounting.from_code !== s.accounting.to_code ? ` (${s.accounting.to_code})` : ""}, {s.accounting.lines} écriture(s), {s.accounting.letterings} lettrage(s)</>}
                      {s.warnings.map(w => <div key={w} style={{ color: "#92400e" }}>⚠ {w}</div>)}
                      {s.blockers.map(b => <div key={b} style={{ color: "#b91c1c" }}>⛔ {b}</div>)}
                    </div>
                  ))}
                  {isAdmin
                    ? <button className="btn primary" style={{ marginTop: 8 }} disabled={busy || mergePlan.blocked} onClick={confirmMerge}>✓ Confirmer la fusion</button>
                    : <div style={{ marginTop: 8, color: "#4b5563" }}>La confirmation est réservée à l'administrateur de la société.</div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {plan && (
        <div style={{ ...box, background: "#eff6ff" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Renommage des comptes clients et fournisseurs — aperçu</div>
          {plan.items.length === 0 ? <div>Aucun compte à renommer : tous les comptes portent déjà le code de l'expert-comptable.</div> : (
            <>
              <div style={{ marginBottom: 6 }}><strong>{plan.ready} compte(s) prêt(s)</strong>{plan.blocked ? ` · ${plan.blocked} bloqué(s) (motif indiqué)` : ""}. Les montants ne changent pas ; les écritures, lettrages, lignes de relevé et règles apprises suivent le nouveau code.</div>
              <table className="table" style={{ width: "100%", fontSize: 12 }}>
                <thead><tr><th style={{ textAlign: "left" }}>Tiers</th><th style={{ textAlign: "left" }}>Compte actuel</th><th style={{ textAlign: "left" }}>Nouveau code</th><th>Écritures</th><th>Lettrages</th><th>Relevé</th><th>Règles</th><th style={{ textAlign: "left" }}>État</th></tr></thead>
                <tbody>{plan.items.map(i => (
                  <tr key={`${i.kind}${i.account_id}`}><td>{i.customer} <span style={{ color: "#6b7280" }}>({i.kind})</span></td><td>{i.from_code}</td><td><strong>{i.to_code}</strong></td>
                    <td style={{ textAlign: "center" }}>{i.lines}</td><td style={{ textAlign: "center" }}>{i.letterings}</td><td style={{ textAlign: "center" }}>{i.pending_bank_lines}</td><td style={{ textAlign: "center" }}>{i.rules}</td>
                    <td style={{ color: i.blockers.length ? "#b91c1c" : "#166534" }}>{i.blockers.length ? `⚠ ${i.blockers.join(" · ")}` : "prêt"}
                      {i.joined_lines > 0 && !i.blockers.length && <div style={{ color: "#92400e" }}>réunit {i.joined_lines} écriture(s) déjà sur {i.to_code} (ex. encaissements saisis au relevé)</div>}</td></tr>
                ))}</tbody>
              </table>
              {isAdmin
                ? <button className="btn primary" style={{ marginTop: 8 }} disabled={busy || !plan.ready} onClick={confirmRename}>✓ Confirmer le renommage ({plan.ready} compte(s))</button>
                : <div style={{ marginTop: 8, color: "#4b5563" }}>La confirmation est réservée à l'administrateur de la société.</div>}
            </>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        {["clients", "fournisseurs"].map(v => <button key={v} className={`btn${view === v ? " primary" : ""}`} onClick={() => { setView(v); setSearch(""); }}>{v === "clients" ? `Clients (${data.clients.length})` : `Fournisseurs (${(data.suppliers || []).length})`}</button>)}
        {reprise && (
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12, color: pendingTotal ? "#92400e" : "#4b5563" }}>
            <input type="checkbox" checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} />
            Code expert à attribuer ({pendingCount(suppliers ? data.suppliers || [] : data.clients)})
          </label>
        )}
        <input className="input" placeholder={`Rechercher un ${suppliers ? "fournisseur" : "client"}, un code ou un compte`} value={search} onChange={e => setSearch(e.target.value)} style={{ width: "100%", maxWidth: 360 }} />
      </div>
      <table className="table" style={{ width: "100%", fontSize: 12 }}>
        <thead><tr><th style={{ textAlign: "left" }}>{suppliers ? "Fournisseur" : "Client"}</th><th style={{ textAlign: "left" }}>Compte actuel</th><th style={{ textAlign: "left" }}>Code de l'expert-comptable</th><th>Écritures</th><th style={{ textAlign: "left" }}>Statut</th></tr></thead>
        <tbody>{rows.slice(0, 500).map(c => (
          <tr key={c.key}>
            <td>{c.name}</td>
            <td>{c.account || "—"}</td>
            <td>
              {canEdit ? (
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                  <input className="input" style={{ width: 130, padding: "2px 6px" }} placeholder={suppliers ? "401…" : "411…"} value={edits[c.key] ?? c.code ?? ""} onChange={e => setEdits(x => ({ ...x, [c.key]: e.target.value.toUpperCase().replace(/\s/g, "") }))} />
                  {edits[c.key] !== undefined && edits[c.key] !== (c.code || "") && <button className="btn" style={{ padding: "2px 8px" }} disabled={busy || !edits[c.key]} onClick={() => saveCode(c)}>Enregistrer</button>}
                </span>
              ) : (c.code || "—")}
            </td>
            <td style={{ textAlign: "center" }}>{c.lines}</td>
            <td style={{ color: STATUS_COLORS[c.status] || "#4b5563" }}>{c.status}{c.flags.filter(f => f !== "sans code").length ? ` · ⚠ ${c.flags.filter(f => f !== "sans code").join(", ")}` : ""}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
